import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView, Alert } from 'react-native';
import AppTextInput from './AppTextInput';
import type { CareMedication } from '../types/care';

interface CareAddMedicationModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (med: Omit<CareMedication, 'id' | 'createdAt'>) => void;
}

const PRESET_SCHEDULES = [
  { label: 'Morning (8:00 AM)', times: ['08:00'] },
  { label: 'Evening (8:00 PM)', times: ['20:00'] },
  { label: 'Morning & Evening', times: ['08:00', '20:00'] },
  { label: 'Three times daily', times: ['08:00', '14:00', '20:00'] },
];

const CareAddMedicationModal: React.FC<CareAddMedicationModalProps> = ({
  visible,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [scheduleLabel, setScheduleLabel] = useState(PRESET_SCHEDULES[0].label);
  const [reminderTimes, setReminderTimes] = useState<string[]>(PRESET_SCHEDULES[0].times);
  const [reminderEnabled, setReminderEnabled] = useState(true);

  const handlePreset = (label: string, times: string[]) => {
    setScheduleLabel(label);
    setReminderTimes(times);
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      Alert.alert('Required', 'Please enter the medication name.');
      return;
    }
    onSave({
      name: trimmed,
      scheduleLabel,
      reminderTimes: [...reminderTimes],
      reminderEnabled,
    });
    setName('');
    setScheduleLabel(PRESET_SCHEDULES[0].label);
    setReminderTimes(PRESET_SCHEDULES[0].times);
    setReminderEnabled(true);
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>Add medication</Text>
            <TouchableOpacity onPress={onClose} hitSlop={12}>
              <Text style={styles.close}>✕</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.disclaimer}>
            Coordination only — no dosage or medical advice. We track compliance (taken/missed).
          </Text>
          <ScrollView style={styles.body}>
            <Text style={styles.label}>Medication name</Text>
            <AppTextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              placeholder="e.g. Daily vitamin"
              autoCapitalize="none"
            />
            <Text style={styles.label}>Schedule (reminder times)</Text>
            {PRESET_SCHEDULES.map((preset) => (
              <TouchableOpacity
                key={preset.label}
                style={[
                  styles.presetRow,
                  scheduleLabel === preset.label && styles.presetRowSelected,
                ]}
                onPress={() => handlePreset(preset.label, preset.times)}
              >
                <Text style={styles.presetLabel}>{preset.label}</Text>
              </TouchableOpacity>
            ))}
            <View style={styles.reminderRow}>
              <TouchableOpacity
                style={[styles.checkbox, reminderEnabled && styles.checkboxChecked]}
                onPress={() => setReminderEnabled(!reminderEnabled)}
              >
                {reminderEnabled && <Text style={styles.checkmark}>✓</Text>}
              </TouchableOpacity>
              <Text style={styles.reminderLabel}>Send push/SMS reminders</Text>
            </View>
          </ScrollView>
          <View style={styles.footer}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.saveBtn} onPress={handleSave}>
              <Text style={styles.saveBtnText}>Add</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  card: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '85%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 8,
  },
  title: { fontSize: 20, fontWeight: '700', color: '#1C1C1E' },
  close: { fontSize: 22, color: '#8E8E93', padding: 4 },
  disclaimer: {
    fontSize: 12,
    color: '#8E8E93',
    fontStyle: 'italic',
    paddingHorizontal: 20,
    paddingBottom: 12,
  },
  body: { paddingHorizontal: 20, paddingBottom: 16 },
  label: { fontSize: 14, fontWeight: '600', color: '#1C1C1E', marginBottom: 6 },
  input: {
    borderWidth: 1,
    borderColor: '#E5E5E7',
    borderRadius: 10,
    padding: 12,
    fontSize: 16,
    color: '#1C1C1E',
    marginBottom: 16,
  },
  presetRow: {
    padding: 12,
    borderRadius: 10,
    backgroundColor: '#F2F2F7',
    marginBottom: 8,
  },
  presetRowSelected: { backgroundColor: '#E8F4FF', borderWidth: 1, borderColor: '#007AFF' },
  presetLabel: { fontSize: 15, color: '#1C1C1E' },
  reminderRow: { flexDirection: 'row', alignItems: 'center', marginTop: 12, marginBottom: 8 },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#C7C7CC',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  checkboxChecked: { backgroundColor: '#007AFF', borderColor: '#007AFF' },
  checkmark: { color: '#fff', fontWeight: '700' },
  reminderLabel: { fontSize: 15, color: '#1C1C1E' },
  footer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E7',
  },
  cancelBtn: { flex: 1, padding: 14, alignItems: 'center', borderRadius: 10, backgroundColor: '#F2F2F7' },
  cancelBtnText: { fontSize: 16, fontWeight: '600', color: '#1C1C1E' },
  saveBtn: { flex: 1, padding: 14, alignItems: 'center', borderRadius: 10, backgroundColor: '#007AFF' },
  saveBtnText: { fontSize: 16, fontWeight: '600', color: '#fff' },
});

export default CareAddMedicationModal;
